import axios from 'axios';
import { FIRM_SETTINGS } from 'constants/constants';

export interface CpaSettingResponse {
  data: string | boolean;
}

export interface UpdateCpaSettingResponse {
  value: string | boolean;
}

const SETTING_KEYS = {
  KBA: 'tax_delivery_kba',
  REMINDER_TYPE: 'tax_delivery_reminder_type',
} as const;

export const REMINDER_TYPE_OPTIONS = [
  {
    value: 'remind_to_pay',
    label: 'Remind to Pay',
    description:
      'Send a reminder to the client when estimated tax payments are due.',
  },
  {
    value: 'notify_withdrawal',
    label: 'Notify of Withdrawal',
    description:
      'Notify the client that the firm will withdraw estimated tax payments on their behalf.',
  },
  {
    value: 'no_reminder',
    label: "Don't Remind",
    description: 'No notification will be sent for estimated tax payments.',
  },
] as const;

export type ReminderType = (typeof REMINDER_TYPE_OPTIONS)[number]['value'];

/**
 * Fetches the KBA setting for tax delivery
 */
export async function getTaxDeliveryKba(): Promise<boolean> {
  const response = await axios.get<CpaSettingResponse>(
    `${FIRM_SETTINGS}/${SETTING_KEYS.KBA}`,
  );
  return Boolean(response.data.data);
}

/**
 * Updates the KBA setting for tax delivery
 */
export async function updateTaxDeliveryKba(
  enabled: boolean,
): Promise<boolean> {
  const response = await axios.put<UpdateCpaSettingResponse>(
    `${FIRM_SETTINGS}/${SETTING_KEYS.KBA}`,
    {
      type: 'boolean',
      value: enabled,
    },
  );
  return Boolean(response.data.value);
}

/**
 * Fetches the reminder type setting for tax delivery
 */
export async function getReminderType(): Promise<ReminderType> {
  const response = await axios.get<CpaSettingResponse>(
    `${FIRM_SETTINGS}/${SETTING_KEYS.REMINDER_TYPE}`,
  );
  return response.data.data as ReminderType;
}

/**
 * Updates the reminder type setting for tax delivery
 */
export async function updateReminderType(
  reminderType: ReminderType,
): Promise<ReminderType> {
  const response = await axios.put<UpdateCpaSettingResponse>(
    `${FIRM_SETTINGS}/${SETTING_KEYS.REMINDER_TYPE}`,
    {
      type: 'string',
      value: reminderType,
    },
  );
  return response.data.value as ReminderType;
}
